import { create } from 'zustand';
import axios from '../axios-base';
import { transformPhases, ProcessedPhases } from '../shared/phaseHelper';

export type PhaseType = 'signup' | 'qualification' | 'finale';

export interface Phase {
    _id: string;
    start: string;
    end: string;
    type: PhaseType;
}

export type FeatureFlagName = 'memoryGame';

export type FeatureFlags = Partial<Record<FeatureFlagName, boolean | null | undefined>>;

export interface JuryFundingOption {
    displayValue: string;
    amount: number;
}

export interface RawClientConfiguration {
    _id: string;
    phases: Phase[];
    featureFlags: FeatureFlags;
    finalistCount: number;
    joinedCategories: boolean;
    juryFundingOptions?: JuryFundingOption[];
    isJuryVisible: boolean;
}

export interface ClientConfig extends Omit<RawClientConfiguration, 'phases'> {
    phases: ProcessedPhases;
}

interface ConfigState {
    config: ClientConfig | null;
    loading: boolean;
    error: Error | null;
}

interface ConfigActions {
    fetchConfig: () => Promise<void>;
}

export type ConfigStore = ConfigState & ConfigActions;

export const useConfigStore = create<ConfigStore>((set) => ({
    config: null,
    loading: true,
    error: null,

    fetchConfig: async () => {
        try {
            set({ loading: true, error: null });
            const response = await axios.get<RawClientConfiguration>('/config');
            const processedConfig: ClientConfig = {
                ...response.data,
                phases: transformPhases(response.data.phases),
            };
            set({ config: processedConfig, loading: false });
        } catch (err) {
            console.error('Failed to fetch configuration:', err);
            set({
                error: err instanceof Error ? err : new Error(String(err)),
                loading: false,
            });
        }
    },
}));

/**
 * Helper to safely get the client config and throw an error if not yet loaded.
 */
const useSafeConfig = (): ClientConfig => {
    const config = useConfigStore((state) => state.config);
    if (!config) {
        throw new Error('ConfigStore is null. Make sure the configuration finished loading.');
    }
    return config;
};

// Strongly-typed hooks to expose specific configuration properties
export const usePhases = (): ProcessedPhases => useSafeConfig().phases;

export const useFeatureFlags = (): FeatureFlags => useSafeConfig().featureFlags ?? {};

export const useFeatureFlag = (flag: FeatureFlagName): boolean => {
    const config = useSafeConfig();
    return Boolean(config.featureFlags?.[flag] === true);
};

export const useFinalistCount = (): number => useSafeConfig().finalistCount;

export const useJoinedCategories = (): boolean => useSafeConfig().joinedCategories;

export const useIsJuryVisible = (): boolean => useSafeConfig().isJuryVisible;

export const useIsMemoryGameActive = (): boolean => {
    return useFeatureFlag('memoryGame');
};

export const useJuryFundingOptions = (): JuryFundingOption[] => useSafeConfig().juryFundingOptions ?? [];
